/**
 * Parser for multiple sources (JavaScript version)
 */

/**
 * KodikParser
 * Parser for Kodik source
 * Most logic is based on KodikWrapper (https://github.com/thedvxchsquad/kodikwrapper/blob/master/src/video-links.ts)
 */

/**
 * @typedef {('240'|'360'|'480'|'720'|'1080'|string)} KodikQuality
 *
 * @typedef {{src:string, type:string}} KodikVideoSource
 * @typedef {Record<KodikQuality, KodikVideoSource[]>} KodikVideoLinks
 *
 * @typedef {Object} KodikVast
 * @property {string} title_small
 * @property {string} src
 * @property {number} [timer]
 * @property {boolean} [hide_interface]
 * @property {boolean} [async_load]
 * @property {string} [vpaid_target_event]
 * @property {number} [vpaid_max_load_time]
 * @property {number} [vpaid_max_start_time]
 * @property {string} [vpaid_start_event]
 * @property {string} [vpaid_timer_start_event]
 * @property {boolean} [vpaid_ad_skippable_state]
 * @property {string} [advert_id]
 * @property {boolean} [save_views]
 * @property {boolean} [start_muted]
 * @property {number} [max_length]
 * @property {number} [disable_advert_click]
 * @property {string} [send_stat_method]
 * @property {boolean} [stop_timer_on_pause]
 *
 * @typedef {Object} KodikDirectLinkResponse
 * @property {string} advert_script
 * @property {string} domain
 * @property {number} default
 * @property {KodikVideoLinks} links
 * @property {KodikVast[]} vast
 * @property {KodikVast[]} reserve_vast
 * @property {string} ip
 */

export class KodikParser {
  static _baseKodikDomain = 'kodik.temaflux.pp.ua';
  static _endpointUrl = '/ftor';

  /**
   * Get latest endpoint path encoded in page's player script
   * @param {string} url
   * @returns {Promise<string|null>}
   */
  static async getLatestLink(url) {
    const endpointUrlRegex = new RegExp(/url:atob\(\"(?<encodedPath>[^"]+)\"\)/is);
    const appPlayerPathRegex = new RegExp(/src="(?<path>\/assets\/js\/app\.player_single\..*?\.js)"\>/is);

    const playerResponse = await (await fetch(url)).text();
    const appPlayerPath = playerResponse.match(appPlayerPathRegex)?.groups?.path;

    if (!appPlayerPath) return null;

    const host = new URL(url).host;
    const appPlayerResponse = await (await fetch(`https://${host}${appPlayerPath}`)).text();
    const latestEndpoint = appPlayerResponse.match(endpointUrlRegex)?.groups?.encodedPath;

    if (!latestEndpoint) return null;

    return atob(latestEndpoint);
  }

  /**
   * Request direct Kodik links and decode obfuscated sources if needed
   * @param {string} url
   * @param {string} [endpointPath=this._endpointUrl]
   * @returns {Promise<KodikVideoLinks|null>}
   */
  static async getDirectLinks(url, endpointPath = this._endpointUrl) {
    const urlResponse = await (await fetch(url)).text();

    const paramsMatch = urlResponse.match(/var\surlParams\s=\s'(?<params>.*?)';/is);
    const urlParams = JSON.parse(paramsMatch?.groups?.params ?? "{}");

    const videoInfoHash = urlResponse.match(/\w+.hash\s=\s'(?<hash>.*?)';/is)?.groups?.hash;
    const videoInfoId = urlResponse.match(/\w+.id\s=\s'(?<id>.*?)';/is)?.groups?.id;
    const videoInfoType = urlResponse.match(/\w+.type\s=\s'(?<type>.*?)';/)?.groups?.type;
    const validKodikUrl = new RegExp(/\/\/cloud-kodik\.temaflux\.pp\.ua\/useruploads\/.*?\/.*?\/(240|360|480|720|1080)\.mp4:hls:manifest.m3u8/s);

    if (!videoInfoHash || !videoInfoId || !videoInfoType) return null;

    const requestBody = {
      // ...urlParams,
      type: videoInfoType,
      hash: videoInfoHash,
      id: videoInfoId
    };

    const directLinksResponse = await fetch(`https://${this._baseKodikDomain}${endpointPath}?${new URLSearchParams(requestBody).toString()}`, {
      referrer: '',
      referrerPolicy: "no-referrer"
    });

    if (!directLinksResponse.headers.get("content-type")?.includes("application/json")) return null;

    /** @type {KodikDirectLinkResponse} */
    const directLinks = await directLinksResponse.json();
    const zCharCode = 'Z'.charCodeAt(0);

    for (const [, sources] of Object.entries(directLinks.links)) {
      for (const source of sources) {
        if (validKodikUrl.test(source.src)) continue;

        const decryptedBase64 = source.src.replace(/[a-zA-Z]/g, (e) => {
          let eCharCode = e.charCodeAt(0);
          // shift by 18 and wrap inside A-Z or a-z ranges
          return String.fromCharCode((eCharCode <= zCharCode ? 90 : 122) >= (eCharCode = eCharCode + 18) ? eCharCode : eCharCode - 26);
        });

        source.src = atob(decryptedBase64);
      }
    }

    return directLinks.links;
  }
}

/**
 * AniLibriaParser
 * Parser for Libria source
 */

/**
 * @typedef {Object} AniLibriaAnime
 * (fields omitted in JSDoc for brevity — the parser only needs .episodes)
 *
 * @typedef {Object} AniLibriaEpisode
 * @property {number} id
 * @property {string|null} name
 * @property {number} ordinal
 * @property {{src:string,preview:string,thumbnail:string,optimized:{src:string,preview:string,thumbnail:string}}} preview
 * @property {string} hls_480
 * @property {string} hls_720
 * @property {string} hls_1080
 * @property {number} duration
 * @property {string} updated_at
 * @property {number} sort_order
 * @property {number} release_id
 * @property {string|null} name_english
 */

/**
 * @typedef {Object<string, {src:string}>} AniLibriaReturnObject
 */

export class AniLibriaParser {
  static _baseAniLibriaDomain = 'libria.temaflux.pp.ua';
  static _endpointUrl = '/api/v1/anime/releases';

  static idPattern = new RegExp(/id=(?<id>\d+)/g);
  static episodePattern = new RegExp(/ep=(?<ep>\d+)/g);

  /**
   * Get direct hls links for a given Anilibria release link
   * @param {string} link
   * @returns {Promise<AniLibriaReturnObject|null>}
   */
  static async getDirectLinks(link) {
    const id = this.idPattern.exec(link)?.groups?.id;
    const episode = this.episodePattern.exec(link)?.groups?.ep;

    if (!id) return null;

    const request = await fetch(`https://${this._baseAniLibriaDomain}${this._endpointUrl}/${id}`);
    /** @type {AniLibriaAnime} */
    const body = await request.json();

    if (!body || typeof episode !== 'string') return null;

    const ep = (body.episodes || []).find(e => e.ordinal === parseInt(episode, 10)) ?? null;

    if (ep) {
      return {
        "1080": { src: ep.hls_1080 },
        "720": { src: ep.hls_720 },
        "480": { src: ep.hls_480 }
      };
    }

    return null;
  }
}

/**
 * SibnetParser
 * Parser for Sibnet source
 */

export class SibnetParser {
  static _baseSibnetDomain = 'video.sibnet.ru';
  static srcMatch = new RegExp(/src: (".*?")/g);

  /**
   * Get direct link from sibnet page
   * @param {string} link
   * @returns {Promise<string|null>}
   */
  static async getDirectLink(link) {
    const request = await fetch(link);
    const body = await request.text();
    const match = this.srcMatch.exec(body);

    if (match) {
      const path = match[1].replace(/"/g, '');
      const srcRequest = await fetch(`https://${this._baseSibnetDomain}${path}`, {
        headers: {
          host: this._baseSibnetDomain,
          referer: link
        }
      });

      return srcRequest.url ?? null;
    }

    return null;
  }
}
